import { useTranslation } from "react-i18next";
import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const numberLocale = { fr: "fr-FR", en: "en-US" };

export function KpiCard({ icon, label, value, growthPct, color = "#E32636", testid }) {
  const { t } = useTranslation();
  const g = growthPct;
  const gColor = g == null ? "#4A4A4A" : g > 0 ? "#009E60" : g < 0 ? "#E32636" : "#4A4A4A";
  const Arrow = g == null ? Minus : g > 0 ? TrendingUp : g < 0 ? TrendingDown : Minus;
  return (
    <div className="bg-white border border-sand rounded-2xl p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest flex items-center gap-1.5 mb-1" style={{ color }}>
        {icon} {label}
      </div>
      <div className="font-display text-xl sm:text-2xl font-semibold">{value}</div>
      {g != null && (
        <div className="flex items-center gap-1 text-[11px] mt-1" style={{ color: gColor }}>
          <Arrow className="w-3 h-3" /> {g > 0 ? "+" : ""}{g}% {t("ddw.vsPrev")}
        </div>
      )}
    </div>
  );
}

/**
 * Heatmap 7 jours × 24 heures.
 * data: number[7][24] where row 0 = Sunday.
 */
export function OrderHeatmap({ data, lang = "fr" }) {
  const DAYS = { fr: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"], en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] };
  const days = DAYS[lang] || DAYS.fr;
  const flat = data.flat();
  const max = Math.max(...flat, 1);

  const cellColor = (v) => {
    if (v === 0) return "#F8F5EE"; // sand
    const pct = v / max;
    // Interpolate from cream to brand-red
    const r = Math.round(253 - (253 - 227) * pct);
    const g = Math.round(251 - (251 - 38) * pct);
    const b = Math.round(247 - (247 - 54) * pct);
    return `rgb(${r},${g},${b})`;
  };

  return (
    <div className="bg-white border border-sand rounded-2xl p-4 overflow-x-auto" data-testid="order-heatmap">
      <table className="text-[10px] font-medium border-separate" style={{ borderSpacing: 2 }}>
        <thead>
          <tr>
            <th></th>
            {Array.from({ length: 24 }, (_, h) => (
              <th key={h} className="text-mute font-normal w-6 text-center">{h % 3 === 0 ? h : ""}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {days.map((d, dow) => (
            <tr key={d}>
              <td className="text-mute pr-2 text-right w-8">{d}</td>
              {Array.from({ length: 24 }, (_, h) => {
                const v = data[dow]?.[h] || 0;
                return (
                  <td
                    key={h}
                    className="w-6 h-6 rounded transition-colors"
                    style={{ backgroundColor: cellColor(v) }}
                    title={`${d} ${h}h — ${v} commande${v > 1 ? "s" : ""}`}
                    data-testid={`heat-${dow}-${h}`}
                  >
                    <span className="sr-only">{v}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RangeSelector({ value, onChange, options = [7, 14, 30, 90], testidPrefix = "range" }) {
  return (
    <div className="inline-flex bg-white rounded-full border border-sand p-1 text-xs">
      {options.map((d) => (
        <button
          key={d}
          onClick={() => onChange(d)}
          data-testid={`${testidPrefix}-${d}`}
          className={`rounded-full px-3 py-1 font-semibold ${value === d ? "bg-ink text-white" : "text-ink hover:bg-elevated"}`}
        >
          {d}j
        </button>
      ))}
    </div>
  );
}

export function fmtNumber(n, lang = "fr") {
  return new Intl.NumberFormat(numberLocale[lang] || "fr-FR").format(n || 0);
}
